import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Card, CardBody, CardHeader, Col, Row, Button, Spinner } from "reactstrap";
import { getUrlPrefirmada, verificarDocumentosSolicitud } from "../../../helpers/backend_helper";
import { editSolicitud } from "../../../slices/thunks";
import { solicitudSuccess } from "../../../slices/configuracion/solicitudes/reducer";
import { toast } from "react-toastify";
import FileViewer from "../../../Components/Common/FileViewer";
import { DatosSesion } from "../../../Components/Common/DatosSesion";

// Función para formatear fecha (solo fecha, sin hora)
const formatearFecha = (fecha) => {
  if (!fecha) return '';
  try {
    // Si viene como string con hora, extraer solo la fecha
    const fechaStr = String(fecha);
    if (fechaStr.includes(' ')) {
      return fechaStr.split(' ')[0];
    }
    if (fechaStr.includes('T')) {
      return fechaStr.split('T')[0];
    }
    return fechaStr;
  } catch {
    return fecha;
  }
};

const Adjuntos = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState({
    url: null,
    name: "",
    type: ""
  });
  const [verificando, setVerificando] = useState(false);

  // Obtener datos del usuario logueado (nombre completo)
  const nombreUsuario = `${DatosSesion("nombre")} ${DatosSesion("apellido")}`.trim();

  // Verificar si ya está verificado
  const estaVerificado = solicitud?.verificacionDocumentos?.verificado === true;
  const verificadoPor = solicitud?.verificacionDocumentos?.farmaceutico;
  const fechaVerificacion = solicitud?.verificacionDocumentos?.fecha;

  // Formatear fecha de verificación (formato Venezuela: DD/MM/YYYY HH:MM am/pm)
  const formatearFechaVerificacion = (fecha) => {
    if (!fecha) return '';
    try {
      // Asegurar que la fecha se interprete como UTC
      // Si no tiene 'Z' al final, agregarla para indicar UTC
      let fechaStr = fecha;
      if (typeof fecha === 'string' && !fecha.endsWith('Z') && !fecha.includes('+')) {
        fechaStr = fecha + 'Z';
      }
      
      const date = new Date(fechaStr);
      const dia = date.toLocaleDateString('es-VE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Caracas'
      });
      const hora = date.toLocaleTimeString('es-VE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Caracas'
      });
      return `${dia} ${hora}`;
    } catch {
      return fecha;
    }
  };

  // Handler para verificar documentos
  const handleVerificarDocumentos = async () => {
    if (!nombreUsuario) {
      toast.error("No se pudo obtener el usuario actual");
      return;
    }

    setVerificando(true);
    try {
      // Usar endpoint específico para verificar documentos
      const verificacion = {
        verificado: true,
        farmaceutico: nombreUsuario,
        fecha: new Date().toISOString()
      };

      const response = await verificarDocumentosSolicitud(solicitud.id, verificacion);
      
      // Actualizar estado local con la solicitud actualizada
      if (response && response.data) {
        // El reducer espera: action.payload.data.data
        dispatch(solicitudSuccess({
          data: {
            data: response.data.data || response.data
          },
          status: 200
        }));
      }
      
      toast.success("✓ Documentos verificados correctamente", {
        position: "top-right",
        autoClose: 3000
      });
    } catch (error) {
      console.error("Error al verificar documentos:", error);
      toast.error("Error al verificar documentos");
    } finally {
      setVerificando(false);
    }
  };

  const createBlobUrl = (base64Data) => {
    // Validar que sea una cadena y que tenga el formato correcto
    if (!base64Data || typeof base64Data !== 'string') {
      console.error('Datos inválidos:', base64Data);
      return null;
    }

    try {
      // Verificar si es una cadena base64 válida con formato data:image/...;base64,...
      if (!base64Data.includes(';base64,')) {
        console.error('Formato base64 inválido:', base64Data);
        return null;
      }

      const parts = base64Data.split(';base64,');
      const contentType = parts[0].split(':')[1];
      const raw = window.atob(parts[1]);
      const rawLength = raw.length;
      const uInt8Array = new Uint8Array(rawLength);

      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
      }

      const blob = new Blob([uInt8Array], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error al crear Blob URL:', error);
      return null;
    }
  };

  // Función para determinar si es una imagen
  const isImageFile = (fileData) => {
    if (!fileData) return false;
    
    // Si es una URL
    if (fileData.startsWith('http')) {
      const extension = fileData.split('?')[0].toLowerCase();
      return extension.endsWith('.jpg') || 
             extension.endsWith('.jpeg') || 
             extension.endsWith('.png') || 
             extension.endsWith('.gif') || 
             extension.endsWith('.webp') ||
             fileData.includes('/image/');
    }
    
    // Si es base64
    return fileData.includes("data:image/") || 
          (fileData.includes("base64") && !fileData.includes("JVBERi0"));
  };

  // Función para determinar si es un PDF
  const isPdfFile = (fileData) => {
    if (!fileData) return false;
    
    // Si es una URL
    if (fileData.startsWith('http')) {
      const extension = fileData.split('?')[0].toLowerCase();
      return extension.endsWith('.pdf') || 
             fileData.includes('/pdf/') ||
             fileData.includes('content-type=application%2Fpdf');
    }
    
    // Si es base64
    return fileData.includes("data:application/pdf") || 
           fileData.includes("JVBERi0"); // Firma de PDF en base64
  };

  // Función para abrir archivo en modal
  const abrirArchivo = (fileData, fileName) => {
    if (!fileData) {
      toast.error("No hay archivo disponible");
      return;
    }
    
    if (fileData.startsWith('http')) {
      // Usar URL prefirmada para archivos en DO Spaces
      getUrlPrefirmada(fileData)
        .then(response => {
          if (response.data && response.data.url) {
            const prefirmedUrl = response.data.url;
            const fileType = isPdfFile(fileData) ? "pdf" : isImageFile(fileData) ? "image" : "other";
            
            setCurrentFile({
              url: prefirmedUrl,
              name: fileName,
              type: fileType
            });
            setModalOpen(true);
          } else {
            toast.error("No se pudo obtener la URL prefirmada");
          }
        })
        .catch(error => {
          console.error("Error:", error);
          toast.error("Error al obtener la URL prefirmada");
        });
    } else if (isImageFile(fileData) || isPdfFile(fileData)) {
      const fileType = isPdfFile(fileData) ? "pdf" : "image";
      setCurrentFile({
        url: fileData,
        name: fileName,
        type: fileType
      });
      setModalOpen(true);
    } else {
      toast.error("Formato de archivo no soportado");
    }
  };

  return (
    <React.Fragment>
      <FileViewer 
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        fileUrl={currentFile.url}
        fileName={currentFile.name}
        fileType={currentFile.type}
      />
      
      <Col>
        <Card>
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-information-line me-1 text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">
                Solicitud de Tipo: {solicitud?.tipo}
              </h5>
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              {/* Recipe - Primera columna */}
              <Col md={6}>
                <div className="d-flex align-items-center border border-dashed p-2 rounded h-100">
                  <div className="flex-shrink-0 avatar-sm">
                    <div className="avatar-title bg-light rounded">
                      <i className="ri-attachment-line fs-20 text-primary"></i>
                    </div>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          abrirArchivo(solicitud.recipe, "Recipe");
                        }}
                      >
                        Recipe
                      </a>
                    </h6>
                    <small className="text-muted">
                      {formatearFecha(solicitud.fechaRecipe)}
                    </small>
                  </div>

                  <div className="hstack gap-3 fs-16">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        abrirArchivo(solicitud.recipe, "Recipe");
                      }}
                      className="text-muted"
                    >
                      <i className="ri-eye-line"></i>
                    </a>
                  </div>
                </div>
              </Col>

              {/* Informe Medico - Segunda columna */}
              <Col md={6}>
                <div className="d-flex align-items-center border border-dashed p-2 rounded h-100">
                  <div className="flex-shrink-0 avatar-sm">
                    <div className="avatar-title bg-light rounded">
                      <i className="ri-attachment-line fs-20 text-primary"></i>
                    </div>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          abrirArchivo(solicitud.informe, "Informe Médico");
                        }}
                      >
                        Informe Médico
                      </a>
                    </h6>
                    <small className="text-muted">
                      {formatearFecha(solicitud.fechaInforme)}
                    </small>
                  </div>

                  <div className="hstack gap-3 fs-16">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        abrirArchivo(solicitud.informe, "Informe Médico");
                      }}
                      className="text-muted"
                    >
                      <i className="ri-eye-line"></i>
                    </a>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Sección de Verificación de Documentos */}
            <Row className="mt-3">
              <Col>
                {estaVerificado ? (
                  // Estado: Verificado
                  <div className="d-flex align-items-center justify-content-between p-3 bg-success-subtle rounded border border-success-subtle">
                    <div className="d-flex align-items-center">
                      <div className="avatar-xs me-3">
                        <div className="avatar-title bg-success text-white rounded-circle">
                          <i className="ri-checkbox-circle-fill fs-16"></i>
                        </div>
                      </div>
                      <div>
                        <h6 className="mb-0 text-success">Documentos Verificados</h6>
                        <small className="text-muted">
                          Por: {verificadoPor} • {formatearFechaVerificacion(fechaVerificacion)}
                        </small>
                      </div>
                    </div>
                    <span className="badge bg-success">
                      <i className="ri-shield-check-line me-1"></i>
                      Verificado
                    </span>
                  </div>
                ) : (
                  // Estado: Pendiente de verificación
                  <div className="border border-warning rounded overflow-hidden">
                    {/* Encabezado con botón */}
                    <div className="d-flex align-items-center justify-content-between p-3 bg-warning-subtle">
                      <div className="d-flex align-items-center">
                        <div className="avatar-xs me-3">
                          <div className="avatar-title bg-warning text-dark rounded-circle">
                            <i className="ri-error-warning-line fs-16"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-0 text-warning">Pendiente de Verificación</h6>
                          <small className="text-muted">
                            Revise el recipe e informe médico antes de continuar
                          </small>
                        </div>
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleVerificarDocumentos}
                        disabled={verificando || solicitud?.estatus === "Cancelado" || solicitud?.estatus === "Entregado"}
                        className="d-flex align-items-center"
                      >
                        {verificando ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Verificando...
                          </>
                        ) : (
                          <>
                            <i className="ri-checkbox-circle-line me-1"></i>
                            Confirmar Verificación
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Checklist de verificación */}
                    <div className="p-3 border-top" style={{ backgroundColor: '#f8f9fa' }}>
                      <h6 className="text-muted mb-3 d-flex align-items-center" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <i className="ri-checkbox-multiple-line me-2"></i>
                        Verificar antes de confirmar
                      </h6>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-soft-primary text-primary px-3 py-2 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                          <i className="ri-calendar-line me-1"></i>
                          Vigencia máx. 6 meses
                        </span>
                        <span className="badge bg-soft-primary text-primary px-3 py-2 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                          <i className="ri-user-line me-1"></i>
                          Datos del asegurado
                        </span>
                        <span className="badge bg-soft-primary text-primary px-3 py-2 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                          <i className="ri-stamp-line me-1"></i>
                          Sello del médico
                        </span>
                        <span className="badge bg-soft-primary text-primary px-3 py-2 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                          <i className="ri-calendar-check-line me-1"></i>
                          Fecha legible
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

// Optimización: React.memo para evitar re-renders innecesarios
export default React.memo(Adjuntos, (prevProps, nextProps) => {
  // Re-renderizar si cambia el ID, la fecha de actualización, o el estado de verificación
  const idIgual = prevProps.solicitud?.id === nextProps.solicitud?.id;
  const actualizadoIgual = prevProps.solicitud?.actualizado === nextProps.solicitud?.actualizado;
  const verificacionIgual = prevProps.solicitud?.verificacionDocumentos?.verificado === nextProps.solicitud?.verificacionDocumentos?.verificado;
  const fechaVerificacionIgual = prevProps.solicitud?.verificacionDocumentos?.fecha === nextProps.solicitud?.verificacionDocumentos?.fecha;
  
  // Retornar true solo si TODO es igual (no re-renderizar)
  // Retornar false si algo cambió (sí re-renderizar)
  return idIgual && actualizadoIgual && verificacionIgual && fechaVerificacionIgual;
});
